// EP-161 S2: the expanded row of ที่จอดรถวันนี้. The slip, the car photo the
// visitor took before it (with the server's receive time, to the second), the
// entry and cash photos, the ML reading for reference, and every decision.
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Lightbox, Skeleton } from '@/components/ui'
import { SlipReferenceBlock } from '@/components/payments/SlipReferenceBlock'
import { parkingImageUrl, useParkingSession } from '@/hooks/useParkingSessions'
import { useDateTimeFormatter } from '@/hooks/useDateFormatter'

interface Photo {
  key: string
  label: string
  /** Server receive time (the stamp on the photo), shown to the second. */
  receivedAt?: string | null
}

export function SessionDetail({ sessionId }: { sessionId: number }) {
  const { t } = useTranslation('parking')
  const { t: tp } = useTranslation('payments')
  const formatDateTime = useDateTimeFormatter()
  const { data: session, isLoading } = useParkingSession(sessionId)
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)

  if (isLoading || !session) return <Skeleton className="h-40" />

  // Seconds do not depend on the timezone (every zone offset is whole
  // minutes), so the account-zone date and minute plus the UTC second is exact.
  const toTheSecond = (iso: string) =>
    `${formatDateTime(iso)}:${String(new Date(iso).getUTCSeconds()).padStart(2, '0')}`

  const payment = session.payment
  const photos: Photo[] = []
  if (payment?.slip_image_key) photos.push({ key: payment.slip_image_key, label: t('sessions.slip') })
  const carKey = session.location_photo_stamped_key ?? session.location_photo_key
  if (carKey) photos.push({ key: carKey, label: t('sessions.car_photo'), receivedAt: session.location_photo_received_at })
  if (session.entry_image_key) photos.push({ key: session.entry_image_key, label: t('sessions.entry_photo') })
  const cashKey = session.cash_photo_stamped_key ?? session.cash_photo_key
  if (cashKey) photos.push({ key: cashKey, label: t('sessions.cash_photo'), receivedAt: session.cash_photo_received_at })

  return (
    <div className="grid gap-4 lg:grid-cols-2 py-2" data-testid={`parking-session-detail-${session.id}`}>
      <div className="flex flex-wrap gap-3">
        {photos.map((photo, i) => (
          <figure key={photo.key} className="space-y-1">
            <button
              type="button"
              className="block border border-base-300 rounded-lg overflow-hidden hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-primary"
              onClick={() => setLightboxIndex(i)}
              aria-label={photo.label}
            >
              <img src={parkingImageUrl(photo.key)} alt={photo.label} className="h-32 w-auto object-cover" loading="lazy" />
            </button>
            <figcaption className="text-xs text-base-content/70">
              {photo.label}
              {photo.receivedAt && (
                <span className="block">
                  {toTheSecond(photo.receivedAt)} {t('sessions.server_time')}
                </span>
              )}
            </figcaption>
          </figure>
        ))}
      </div>

      <div className="space-y-3 min-w-0">
        {payment?.promptpay_ref1 && (
          <p className="text-sm">
            {t('sessions.reference')} <span className="font-mono">{payment.promptpay_ref1}</span>
          </p>
        )}
        {payment?.reject_reason && (
          <p className="text-sm">
            {t('sessions.reject_reason')}: {payment.reject_reason}
          </p>
        )}
        {session.fraud_flagged && (
          <p className="text-sm text-error">
            {t('sessions.fraud_badge')}
            {session.fraud_reason && `: ${session.fraud_reason}`}
            {session.fraud_flagged_by_name && ` · ${t('sessions.fraud_by', { name: session.fraud_flagged_by_name })}`}
            {session.fraud_flagged_at && ` · ${formatDateTime(session.fraud_flagged_at)}`}
          </p>
        )}
        {payment && (
          <SlipReferenceBlock
            gateEvidence={payment.gate_evidence}
            fraudSuspect={payment.fraud_suspect}
            reading={session.readings_ml}
          />
        )}
        {session.decisions.length > 0 && (
          <section aria-label={t('sessions.history')}>
            <h4 className="text-sm font-semibold mb-1">{t('sessions.history')}</h4>
            <ul className="space-y-1 text-sm">
              {session.decisions.map((decision, i) => (
                <li key={i}>
                  {tp(`verification.statuses.${decision.decision}`)}
                  {decision.by_name && ` · ${decision.by_name}`}
                  {decision.at && ` · ${formatDateTime(decision.at)}`}
                  {decision.reason && ` · ${decision.reason}`}
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>

      {lightboxIndex != null && (
        <Lightbox
          images={photos.map((p) => p.key)}
          index={lightboxIndex}
          onIndexChange={setLightboxIndex}
          onClose={() => setLightboxIndex(null)}
          toUrl={parkingImageUrl}
        />
      )}
    </div>
  )
}
